import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { Button, CardActionArea, Typography } from '@material-ui/core';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import { isTraceBoardItemEqual } from './utils/isTraceBoardItemEqual';
import TraceBoardCard from './TraceBoardCard';
import TraceBoardConnector from './TraceBoardConnector';
import { useTraceBoardContext } from './TraceBoardContext';
const StyledDiv = styled.div `
  position: relative;
  display: flex;
  margin: 12px;
  border-radius: 4px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  background: #f7f8fc;
  &.invisible {
    /* hide visiblity but take up space */
    visibility: hidden;
  }
  .expand-collapse {
    .button {
      display: flex;
      align-items: flex-start;
      padding: 12px 12px;
      width: 100%;
      height: 100%;
      .collapse {
        color: #fec400;
      }
      .expand {
        color: #29cc97;
      }
    }
  }
  .board-column {
    flex: 1;
    min-width: 320px;
    min-height: 48px;
    background: linear-gradient(90deg, #e4f2fd 0%, #d3e8f4 99.94%);
    padding: 10px 20px 10px 10px;
  }
  .connector-map {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
`;
const StyledPlaceholderDiv = styled.div `
  min-height: 100%;
`;
const TraceBoardRow = props => {
    const { row, rowIndex } = props;
    const styleRef = useRef();
    const styleItemsRef = useRef([]);
    const { ref, entry, inView } = useInView({
        threshold: 0,
        rootMargin: '100px'
    });
    const { columns, collapse, setSelected } = useTraceBoardContext();
    const isCollapsed = collapse.isCollapsed(rowIndex);
    const classes = ['board-row'];
    !inView && classes.push('invisible');
    // save minHeight so that hiding the content
    // does not affect container height and the scrollbar
    if (entry) {
        // unset the minHeight value when item is visible
        // so that the container height matches its contents
        styleRef.current = !inView
            ? { width: '100%', minHeight: entry.boundingClientRect.height }
            : {};
    }
    // only render adjacent connectors
    const connectors = useMemo(() => {
        return (row.connectors || []).filter(connector => connector.adjacent);
    }, [row]);
    const getKey = useCallback((connector) => {
        const source = `${connector.sourceColumnId}-${connector.sourceId}`;
        const target = `${connector.targetColumnId}-${connector.targetId}`;
        return `connector-${source}->${target}`;
    }, []);
    return (React.createElement(React.Fragment, null,
        inView && typeof row.heading === 'string' ? (React.createElement(Typography, { variant: 'overline', className: 'row-heading' }, row.heading)) : (inView && row.heading),
        React.createElement(StyledDiv, { ref: ref, style: styleRef.current, className: classes.join(' '), onClick: event => {
                const element = event.target;
                if (element.classList.contains('board-column')) {
                    // only unselect if clicking into the empty area of the column
                    setSelected(undefined);
                }
            } }, inView && (React.createElement(React.Fragment, null,
            React.createElement("svg", { className: 'connector-map' }, connectors.map(connector => (React.createElement(TraceBoardConnector, { key: getKey(connector), row: row, rowIndex: rowIndex, connector: connector })))),
            React.createElement("div", { className: 'expand-collapse' },
                React.createElement(CardActionArea, { className: 'button', onClick: () => collapse.toggle(rowIndex) }, isCollapsed ? (React.createElement(AddCircle, { className: 'expand' })) : (React.createElement(RemoveCircle, { className: 'collapse' })))),
            columns.map(column => (React.createElement(BoardColumn, { key: column.id, column: column, rowIndex: rowIndex, items: row.items.filter(item => item.columnId === column.id), getItemStyle: item => {
                    const style = styleItemsRef.current.find(style => {
                        return isTraceBoardItemEqual(style, item);
                    });
                    return style === null || style === void 0 ? void 0 : style.style;
                }, onSize: (item, height) => {
                    const index = styleItemsRef.current.findIndex(style => {
                        return isTraceBoardItemEqual(style, item);
                    });
                    if (index > -1 && typeof height === 'undefined') {
                        styleItemsRef.current.splice(index, 1);
                        return;
                    }
                    const style = {
                        id: item.id,
                        columnId: item.columnId,
                        style: { minHeight: height }
                    };
                    if (index > -1) {
                        styleItemsRef.current[index] = style;
                    }
                    else {
                        styleItemsRef.current.push(style);
                    }
                } })))))),
        !inView && !styleRef.current && React.createElement(StyledPlaceholderDiv, null)));
};
export default TraceBoardRow;
// default number of cards to display per column in row
const pageSize = 50;
const BoardColumn = props => {
    const { items, column, rowIndex, onSize, getItemStyle } = props;
    const [limit, setLimit] = useState(pageSize);
    const next = useMemo(() => {
        return Math.min(limit + pageSize, items.length);
    }, [items.length, limit]);
    return (React.createElement("div", { className: 'board-column' },
        items.slice(0, limit).map(item => (React.createElement(TraceBoardCard, { item: item, column: column, rowIndex: rowIndex, style: getItemStyle === null || getItemStyle === void 0 ? void 0 : getItemStyle(item), key: `item-${rowIndex}-${column.id}-${item.id}`, onSize: height => onSize === null || onSize === void 0 ? void 0 : onSize(item, height) }))),
        limit < items.length && (React.createElement("div", { className: 'load-more' },
            React.createElement(Button, { fullWidth: true, color: 'primary', onClick: () => setLimit(limit => limit + pageSize) },
                "Show ",
                next,
                " of ",
                items.length)))));
};
//# sourceMappingURL=TraceBoardRow.js.map